import { useCallback, useEffect, useMemo, useState } from 'react';
import type {
  ApiClient, ProfilEintrag, RegelKatalog, RegelKatalogSchritt, RegelLauf, RegelZeitplan,
  RegelZeitplanEingabe, RegelZeitplanSchritt,
} from '../api';
import { IconPlus, IconZeitplan } from '../Icons';
import {
  ConfirmDialog, ContentSection, DataTable, EmptyState, ErrorState, Feld, FormSection,
  StatusBadge, type Spalte,
} from '../muster';

/**
 * ═══════════════════════════════════════════════════════════════════════════
 * EINSTELLUNGEN → WIEDERKEHRENDE EXPORTE → KETTEN (WE1, Eigentümer 22.10.2026)
 * ═══════════════════════════════════════════════════════════════════════════
 *
 * Mo–Fr 22:00 die Modellaufnahmen in fester Reihenfolge (Raum, Tür, Aussparung,
 * Lüftung, Sanitär, Heizung, Sprinkler, Elektro, Außenanlagen), danach die
 * Folgeläufe; Mo 23:00 Hülle und Fremdmodell mit dem Lastenblock. Werte und
 * Reihenfolge sind hier einstellbar.
 *
 * Was NICHT geht: ein Rückschreiben in die Modelle. Der Katalog des Servers
 * kennt keinen solchen Schritt; die Oberfläche zeigt ihn nur grau als „noch
 * nicht freigegeben“, damit niemand ihn sucht.
 *
 * Ändern, Anlegen, Probe und Trockenlauf dürfen ProjectAdmin und SystemAdmin
 * (sonst 403); jede Änderung steht im Prüfprotokoll. Ein veralteter Stand gibt
 * 409 - dann neu laden.
 */

const TAGE = ['Mo', 'Di', 'Mi', 'Do', 'Fr', 'Sa', 'So'];

/** Die Zustände des Servers (RegelStatus) in Worten und Farbe. */
const ZUSTAENDE: Record<string, { text: string; zustand: 'gut' | 'warnung' | 'fehler' | 'info' | 'neutral' }> = {
  wartet: { text: 'wartet', zustand: 'info' },
  laeuft: { text: 'läuft', zustand: 'info' },
  fertig: { text: 'fertig', zustand: 'gut' },
  'fertig-mit-befunden': { text: 'fertig mit Befunden', zustand: 'warnung' },
  'nicht-gestartet': { text: 'nicht gestartet', zustand: 'warnung' },
  ausgelassen: { text: 'ausgelassen', zustand: 'warnung' },
  abgebrochen: { text: 'angehalten', zustand: 'fehler' },
  ok: { text: 'ok', zustand: 'gut' },
  unveraendert: { text: 'unverändert', zustand: 'gut' },
  befund: { text: 'Befund', zustand: 'info' },
  bremse: { text: 'Bremse', zustand: 'warnung' },
  fehler: { text: 'Fehler', zustand: 'fehler' },
  uebersprungen: { text: 'übersprungen', zustand: 'neutral' },
};

const AUSLOESER: Record<string, string> = { zeitplan: 'planmäßig', probe: 'Probe', trocken: 'Trockenlauf' };

export function Zustand({ status, titel }: { status: string; titel?: string }) {
  const z = ZUSTAENDE[status] ?? { text: status, zustand: 'neutral' as const };
  return <StatusBadge zustand={z.zustand} text={z.text} klein titel={titel} />;
}

/** Die Wochentage der Bitmaske (Mo = Bit 0) als Liste "1".."7". */
export function tageAusMaske(maske: number): string[] {
  return TAGE.map((_, i) => i).filter((i) => (maske & (1 << i)) !== 0).map((i) => String(i + 1));
}

export function maskeAusTagen(tage: string[]): number {
  return tage.reduce((m, t) => m | (1 << (Number(t) - 1)), 0);
}

/**
 * Wie spät es zur Ortszeit in UTC ist - am angegebenen Tag (Sommer- und
 * Winterzeit). Nur ein Hinweis; die Fälligkeit rechnet der Server.
 */
export function utcHinweis(uhrzeit: string, zone = 'Europe/Berlin', tag = new Date()): string | null {
  const m = /^(\d{1,2}):(\d{2})$/.exec(uhrzeit);
  if (!m) return null;
  try {
    const teile = new Intl.DateTimeFormat('en-US', { timeZone: zone, timeZoneName: 'shortOffset' })
      .formatToParts(tag).find((p) => p.type === 'timeZoneName')?.value ?? '';
    const o = /GMT([+-])(\d{1,2})(?::(\d{2}))?/.exec(teile);
    const versatz = o ? (o[1] === '-' ? -1 : 1) * (Number(o[2]) * 60 + Number(o[3] ?? 0)) : 0;
    const min = ((Number(m[1]) * 60 + Number(m[2]) - versatz) % 1440 + 1440) % 1440;
    return `${String(Math.floor(min / 60)).padStart(2, '0')}:${String(min % 60).padStart(2, '0')} UTC`;
  } catch {
    return null;
  }
}

/** Ob ein Folgelauf in einem Schritt läuft - dieselbe Regel wie SchrittOptionen.LaufAktiv. */
export function laufAktiv(s: RegelZeitplanSchritt, lauf: { schluessel: string; standardAktiv: boolean }): boolean {
  return !s.aus.includes(lauf.schluessel) && (lauf.standardAktiv || s.an.includes(lauf.schluessel));
}

export function laufSchalten(
  s: RegelZeitplanSchritt, lauf: { schluessel: string; standardAktiv: boolean }, an: boolean,
): RegelZeitplanSchritt {
  const ohne = (l: string[]) => l.filter((x) => x !== lauf.schluessel);
  if (an) {
    return { ...s, aus: ohne(s.aus), an: lauf.standardAktiv ? ohne(s.an) : [...ohne(s.an), lauf.schluessel] };
  }
  return { ...s, an: ohne(s.an), aus: lauf.standardAktiv ? [...ohne(s.aus), lauf.schluessel] : ohne(s.aus) };
}

/** Ein Schritt eine Stelle nach oben (-1) oder unten (+1). */
export function verschieben<T>(liste: T[], i: number, richtung: -1 | 1): T[] {
  const j = i + richtung;
  if (j < 0 || j >= liste.length) return liste;
  const neu = [...liste];
  [neu[i], neu[j]] = [neu[j], neu[i]];
  return neu;
}

function fehlerText(e: unknown): string {
  const t = e instanceof Error ? e.message : String(e);
  return t === 'HTTP 403'
    ? 'Ketten ändern und auslösen dürfen nur ProjectAdmin und SystemAdmin.'
    : t;
}

function zeit(iso: string | null | undefined): string {
  if (!iso) return '—';
  return new Date(iso).toLocaleString('de-DE', {
    timeZone: 'Europe/Berlin', weekday: 'short', day: '2-digit', month: '2-digit', hour: '2-digit', minute: '2-digit',
  });
}

export function Regelaufgaben({
  api, projektId, profile,
}: {
  api: ApiClient;
  projektId: string;
  profile: ProfilEintrag[];
}) {
  const [katalog, setKatalog] = useState<RegelKatalog | null>(null);
  const [plaene, setPlaene] = useState<RegelZeitplan[] | null>(null);
  const [fehler, setFehler] = useState<unknown>(null);
  const [bearbeiten, setBearbeiten] = useState<RegelZeitplan | 'neu' | null>(null);
  const [verlauf, setVerlauf] = useState<string | null>(null);
  const [ausloesen, setAusloesen] = useState<{ z: RegelZeitplan; art: 'probe' | 'trocken' } | null>(null);
  const [entfernen, setEntfernen] = useState<RegelZeitplan | null>(null);
  const [meldung, setMeldung] = useState<string | null>(null);
  const [aktionsfehler, setAktionsfehler] = useState<string | null>(null);

  const laden = useCallback(() => {
    setFehler(null);
    return Promise.all([api.regelaufgabenKatalog(), api.regelaufgaben(projektId)])
      .then(([k, p]) => { setKatalog(k); setPlaene(p); })
      .catch(setFehler);
  }, [api, projektId]);

  useEffect(() => { laden(); }, [laden]);

  const titel = useMemo(() => new Map((katalog?.schritte ?? []).map((s) => [s.schluessel, s.titel])), [katalog]);

  const startwerte = async () => {
    setAktionsfehler(null);
    try {
      const r = await api.regelaufgabenStartwerte(projektId);
      setMeldung(r.angelegt > 0
        ? `${r.angelegt} Kette(n) stillgelegt angelegt.${r.hinweise.length ? ` ${r.hinweise.join(' ')}` : ''}`
        : (r.hinweise.join(' ') || 'Nichts angelegt - die Ketten bestehen schon.'));
      laden();
    } catch (e) {
      setAktionsfehler(fehlerText(e));
    }
  };

  const spalten: Spalte<RegelZeitplan>[] = useMemo(() => [
    {
      schluessel: 'name', titel: 'Kette', immerSichtbar: true,
      wert: (z) => z.name,
      inhalt: (z) => <strong>{z.name}</strong>,
    },
    {
      schluessel: 'wann', titel: 'Wann', breite: '150px',
      wert: (z) => `${z.wochentageText} ${z.uhrzeit}`,
      inhalt: (z) => <span className="schwach">{z.wochentageText} {z.uhrzeit}</span>,
    },
    {
      schluessel: 'schritte', titel: 'Schritte',
      wert: (z) => z.schritte.filter((s) => s.istAktiv).length,
      inhalt: (z) => (
        <span className="schwach" title={z.schritte.map((s) => titel.get(s.schluessel) ?? s.schluessel).join(' → ')}>
          {z.schritte.filter((s) => s.istAktiv).map((s) => titel.get(s.schluessel) ?? s.schluessel).join(' → ')
            || <span className="sehr-schwach">keine</span>}
        </span>
      ),
    },
    {
      schluessel: 'naechster', titel: 'Nächster Lauf', breite: '150px',
      wert: (z) => z.naechsterLaufOrtszeit ?? '',
      inhalt: (z) => (z.istAktiv && z.naechsterLaufOrtszeit
        ? <span className="schwach">{z.naechsterLaufOrtszeit}</span>
        : <StatusBadge zustand="neutral" text="stillgelegt" klein />),
    },
    {
      schluessel: 'zuletzt', titel: 'Zuletzt', breite: '150px',
      wert: (z) => z.letzterLauf?.status ?? '',
      inhalt: (z) => (z.letzterLauf
        ? <Zustand status={z.letzterLauf.status} titel={AUSLOESER[z.letzterLauf.ausloeser] ?? z.letzterLauf.ausloeser} />
        : <span className="sehr-schwach">—</span>),
    },
    {
      schluessel: 'aktionen', titel: '', breite: '330px', sortierbar: false, ausrichtung: 'rechts',
      inhalt: (z) => (
        <span style={{ whiteSpace: 'nowrap' }}>
          <button className="m-knopf" onClick={(e) => { e.stopPropagation(); setBearbeiten(z); }}>Bearbeiten</button>{' '}
          <button className="m-knopf" onClick={(e) => { e.stopPropagation(); setVerlauf(verlauf === z.id ? null : z.id); }}>
            Verlauf
          </button>{' '}
          <button className="m-knopf" onClick={(e) => { e.stopPropagation(); setAusloesen({ z, art: 'trocken' }); }}>
            Trockenlauf
          </button>{' '}
          <button className="m-knopf" onClick={(e) => { e.stopPropagation(); setAusloesen({ z, art: 'probe' }); }}>
            Jetzt ausführen
          </button>{' '}
          <button className="m-knopf" onClick={(e) => { e.stopPropagation(); setEntfernen(z); }}>Entfernen</button>
        </span>
      ),
    },
  ], [titel, verlauf]);

  return (
    <ContentSection
      titel="Ketten: Modellaufnahmen und Folgeläufe"
      beschreibung={'Abends die Aufnahmen in fester Reihenfolge, danach Berechnungen, Berichte und Listen - '
        + 'in Ortszeit, auch über die Zeitumstellung. Eine Kette schreibt nie in die Modelle zurück.'}
      werkzeuge={<IconZeitplan />}
      aktionen={(
        <>
          <button className="m-knopf" onClick={startwerte} disabled={katalog === null}>Startwerte anlegen</button>{' '}
          <button className="m-knopf" onClick={() => setBearbeiten(bearbeiten ? null : 'neu')} disabled={katalog === null}>
            {bearbeiten ? 'Schließen' : <><IconPlus size={16} />Kette anlegen</>}
          </button>
        </>
      )}
    >
      {fehler !== null && (
        <ErrorState klein meldung="Die Ketten konnten nicht geladen werden." technisch={fehler} beiErneutVersuchen={laden} />
      )}
      {meldung && <p className="hinweis" role="status" data-meldung>{meldung}</p>}
      {aktionsfehler && <p className="hinweis warnung" role="alert" data-aktionsfehler>{aktionsfehler}</p>}

      {bearbeiten && katalog && (
        <Kettenformular
          key={bearbeiten === 'neu' ? 'neu' : `${bearbeiten.id}-${bearbeiten.version}`}
          api={api}
          projektId={projektId}
          katalog={katalog}
          profile={profile}
          ausgang={bearbeiten === 'neu' ? null : bearbeiten}
          fertig={(text) => { setBearbeiten(null); setMeldung(text); laden(); }}
        />
      )}

      <DataTable
        spalten={spalten}
        zeilen={plaene ?? []}
        schluessel={(z) => z.id}
        laedt={plaene === null && fehler === null}
        leer={(
          <EmptyState
            icon={<IconZeitplan />}
            titel="Noch keine Kette."
            erklaerung={'„Startwerte anlegen“ legt die beiden Ketten des Eigentümers an (Mo–Fr 22:00 und Mo 23:00), '
              + 'stillgelegt - erst nach der Probe aktivieren.'}
          />
        )}
      />

      {verlauf && <Verlauf key={verlauf} api={api} zeitplanId={verlauf} titel={titel} />}

      {katalog && (
        <div className="hinweis" style={{ opacity: 0.6 }} aria-disabled="true" data-nicht-freigegeben>
          <span className="hinweis-zeichen">i</span>
          <div>
            <strong>{katalog.nichtFreigegebenGrund}</strong>
            <div className="sehr-schwach">
              {katalog.nichtFreigegeben.map((a) => a.name).join(', ')} - nicht wählbar und nicht auslösbar.
            </div>
          </div>
        </div>
      )}

      <Ausloesen
        auftrag={ausloesen}
        api={api}
        fertig={(text) => { setAusloesen(null); setMeldung(text); laden(); }}
        abbrechen={() => setAusloesen(null)}
      />

      <ConfirmDialog
        offen={entfernen !== null}
        art="loeschen"
        titel={`Kette „${entfernen?.name ?? ''}“ entfernen?`}
        text="Der Verlauf der Läufe geht mit; erzeugte Ergebnisse bleiben erhalten."
        beiBestaetigen={async () => {
          const z = entfernen;
          setEntfernen(null);
          if (!z) return;
          setAktionsfehler(null);
          try { await api.regelaufgabeEntfernen(z.id); } catch (e) { setAktionsfehler(fehlerText(e)); }
          laden();
        }}
        beiAbbrechen={() => setEntfernen(null)}
      />
    </ContentSection>
  );
}

/** Probe oder Trockenlauf: mit Grund und Bestätigung (der Server verlangt beides). */
function Ausloesen({
  auftrag, api, fertig, abbrechen,
}: {
  auftrag: { z: RegelZeitplan; art: 'probe' | 'trocken' } | null;
  api: ApiClient;
  fertig: (text: string) => void;
  abbrechen: () => void;
}) {
  const [grund, setGrund] = useState('');
  const [laeuft, setLaeuft] = useState(false);
  const [fehler, setFehler] = useState<unknown>(null);

  useEffect(() => { setGrund(''); setFehler(null); setLaeuft(false); }, [auftrag]);

  const probe = auftrag?.art === 'probe';
  return (
    <ConfirmDialog
      offen={auftrag !== null}
      art={probe ? 'warnung' : 'standard'}
      titel={probe ? `„${auftrag?.z.name ?? ''}“ jetzt ausführen?` : `Trockenlauf für „${auftrag?.z.name ?? ''}“?`}
      text={probe
        ? 'Die Kette läuft sofort echt: Aufnahmen auf den Verarbeitungsmaschinen, danach die Folgeläufe. '
          + 'In den gesperrten Fenstern (02:30–04:00 und 07:00–09:00 UTC) lehnt der Server ab.'
        : 'Nur der Plan und die Bereitschaft je Schritt - es läuft nichts und es wird nichts geschrieben.'}
      bestaetigenText={probe ? 'Ausführen' : 'Trockenlauf starten'}
      laeuft={laeuft}
      fehler={fehler}
      beiAbbrechen={abbrechen}
      beiBestaetigen={async () => {
        if (!auftrag) return;
        if (grund.trim().length < 3) { setFehler(new Error('Bitte einen Grund angeben (mindestens 3 Zeichen).')); return; }
        setLaeuft(true);
        setFehler(null);
        try {
          await api.regelaufgabeAusloesen(auftrag.z.id, { art: auftrag.art, grund: grund.trim(), bestaetigt: true });
          fertig(probe ? 'Probe angestoßen - der Verlauf zeigt den Fortschritt.' : 'Trockenlauf angestoßen.');
        } catch (e) {
          setFehler(new Error(fehlerText(e)));
          setLaeuft(false);
        }
      }}
    >
      <Feld
        art="textfeld"
        beschriftung="Grund"
        wert={grund}
        beiAenderung={(w) => setGrund(w as string)}
        zeilen={2}
        pflicht
        breite="voll"
        platzhalter="z. B. Probelauf vor der Aktivierung"
      />
    </ConfirmDialog>
  );
}

/** Die letzten Läufe einer Kette mit ihren Schritten - die Tageszusammenfassung (E3). */
function Verlauf({ api, zeitplanId, titel }: { api: ApiClient; zeitplanId: string; titel: Map<string, string> }) {
  const [laeufe, setLaeufe] = useState<RegelLauf[] | null>(null);
  const [fehler, setFehler] = useState<unknown>(null);
  const [offen, setOffen] = useState<string | null>(null);
  const [anhaltfehler, setAnhaltfehler] = useState<string | null>(null);

  const laden = useCallback(() => api.regelaufgabenLaeufe(zeitplanId, 10).then((l) => {
    setLaeufe(l);
    setOffen((o) => o ?? l[0]?.id ?? null);
  }).catch(setFehler), [api, zeitplanId]);

  useEffect(() => { laden(); }, [laden]);

  if (fehler) return <ErrorState klein meldung="Der Verlauf konnte nicht geladen werden." technisch={fehler} />;
  if (!laeufe) return <p className="sehr-schwach">Verlauf wird geladen …</p>;
  if (laeufe.length === 0) return <p className="sehr-schwach" data-verlauf>Noch kein Lauf.</p>;

  return (
    <FormSection titel="Verlauf" spalten={1}>
      {anhaltfehler && <p className="hinweis warnung" role="alert">{anhaltfehler}</p>}
      <div data-verlauf style={{ gridColumn: '1 / -1' }}>
        {laeufe.map((l) => (
          <div key={l.id} className="regel-lauf" style={{ marginBottom: '0.75rem' }}>
            <div style={{ display: 'flex', gap: '0.5rem', alignItems: 'center', flexWrap: 'wrap' }}>
              <Zustand status={l.status} />
              <strong>{AUSLOESER[l.ausloeser] ?? l.ausloeser}</strong>
              <span className="schwach">{zeit(l.geplantFuerUtc ?? l.gestartetAm)}</span>
              {l.beendetAm && <span className="sehr-schwach">bis {zeit(l.beendetAm)}</span>}
              {l.meldungVersandt && <span className="sehr-schwach">· Meldung versandt</span>}
              <button className="m-knopf" onClick={() => setOffen(offen === l.id ? null : l.id)}>
                {offen === l.id ? 'Schritte ausblenden' : 'Schritte'}
              </button>
              {(l.status === 'wartet' || l.status === 'laeuft') && (
                <button
                  className="m-knopf"
                  onClick={async () => {
                    setAnhaltfehler(null);
                    try { await api.regelLaufAnhalten(l.id); } catch (e) { setAnhaltfehler(fehlerText(e)); }
                    laden();
                  }}
                >
                  Anhalten
                </button>
              )}
            </div>
            {l.zusammenfassung && <div className="schwach" style={{ whiteSpace: 'pre-line' }}>{l.zusammenfassung}</div>}
            {l.grund && <div className="sehr-schwach">{l.grund}</div>}
            {offen === l.id && (
              <table className="m-tabelle-einfach" data-schritte={l.id}>
                <thead>
                  <tr><th>#</th><th>Schritt</th><th>Was</th><th>Zustand</th><th>Meldung</th></tr>
                </thead>
                <tbody>
                  {l.schritte.map((s) => (
                    <tr key={s.id}>
                      <td>{s.position}</td>
                      <td>{titel.get(s.gruppe) ?? s.gruppe}</td>
                      <td className="schwach">{s.schluessel}</td>
                      <td><Zustand status={s.status} titel={s.rc !== null ? `Rückgabe ${s.rc}` : undefined} /></td>
                      <td className="schwach">{s.meldung ?? ''}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            )}
          </div>
        ))}
      </div>
    </FormSection>
  );
}

function leererSchritt(k: RegelKatalogSchritt): RegelZeitplanSchritt {
  return { schluessel: k.schluessel, istAktiv: true, aus: [], an: [], teileAus: [] };
}

/** Anlegen und Bearbeiten: Zeiten, Mail, Reihenfolge und Schalter je Schritt, Teilaufnahme und Folgelauf. */
export function Kettenformular({
  api, projektId, katalog, profile, ausgang, fertig,
}: {
  api: ApiClient;
  projektId: string;
  katalog: RegelKatalog;
  profile: ProfilEintrag[];
  ausgang: RegelZeitplan | null;
  fertig: (text: string) => void;
}) {
  const [name, setName] = useState(ausgang?.name ?? '');
  const [profil, setProfil] = useState(ausgang?.exportProfileId ?? profile.find((p) => p.versions.length > 0)?.id ?? '');
  const [tage, setTage] = useState<string[]>(tageAusMaske(ausgang?.wochentage ?? 31));
  const [uhrzeit, setUhrzeit] = useState(ausgang?.uhrzeit ?? '22:00');
  const [frist, setFrist] = useState(ausgang?.fristUtc ?? '02:15');
  const [verspaetung, setVerspaetung] = useState<number | null>(ausgang?.verspaetungMinuten ?? 30);
  const [empfaenger, setEmpfaenger] = useState(ausgang?.benachrichtigung ?? '');
  const [woche, setWoche] = useState(ausgang?.wochenuebersicht ?? false);
  const [aktiv, setAktiv] = useState(ausgang?.istAktiv ?? false);
  const [schritte, setSchritte] = useState<RegelZeitplanSchritt[]>(
    ausgang?.schritte.map((s) => ({ ...s, aus: s.aus ?? [], an: s.an ?? [], teileAus: s.teileAus ?? [] })) ?? [],
  );
  const [aufgeklappt, setAufgeklappt] = useState<string | null>(null);
  const [neuerSchritt, setNeuerSchritt] = useState('');
  const [laeuft, setLaeuft] = useState(false);
  const [fehler, setFehler] = useState<string | null>(null);

  const kat = useMemo(() => new Map(katalog.schritte.map((s) => [s.schluessel, s])), [katalog]);
  const frei = katalog.schritte.filter((k) => !schritte.some((s) => s.schluessel === k.schluessel));
  const ersteGesamt = schritte.findIndex((s) => kat.get(s.schluessel)?.art === 'gesamt');
  const aufnahmeNachGesamt = ersteGesamt >= 0
    && schritte.slice(ersteGesamt).some((s) => kat.get(s.schluessel)?.art === 'aufnahme');
  const hinweisUtc = utcHinweis(uhrzeit, katalog.zeitzone);

  const aendern = (i: number, s: RegelZeitplanSchritt) => setSchritte(schritte.map((x, j) => (j === i ? s : x)));

  const speichern = async () => {
    setLaeuft(true);
    setFehler(null);
    const daten: RegelZeitplanEingabe = {
      name: name.trim(),
      istAktiv: aktiv,
      wochentage: maskeAusTagen(tage),
      uhrzeit,
      fristUtc: frist,
      verspaetungMinuten: verspaetung ?? 30,
      benachrichtigung: empfaenger.trim() || null,
      wochenuebersicht: woche,
      exportProfileId: profil,
      schritte: schritte.map(({ schluessel, istAktiv, aus, an, teileAus }) => ({ schluessel, istAktiv, aus, an, teileAus })),
      ...(ausgang ? { version: ausgang.version } : {}),
    };
    try {
      if (ausgang) await api.regelaufgabeAendern(ausgang.id, daten);
      else await api.regelaufgabeAnlegen(projektId, daten);
      fertig(ausgang ? `„${daten.name}“ gespeichert.` : `„${daten.name}“ angelegt.`);
    } catch (e) {
      setFehler(fehlerText(e));
      setLaeuft(false);
    }
  };

  return (
    <FormSection
      titel={ausgang ? `Kette „${ausgang.name}“ bearbeiten` : 'Neue Kette'}
      spalten={2}
      fuss={(
        <button className="m-knopf m-knopf-haupt" onClick={speichern}
                disabled={laeuft || !name.trim() || !profil || tage.length === 0 || schritte.length === 0}>
          {laeuft ? 'wird gespeichert …' : 'Speichern'}
        </button>
      )}
    >
      {fehler && <p className="hinweis warnung" role="alert" style={{ gridColumn: '1 / -1' }}>{fehler}</p>}

      <Feld beschriftung="Name" wert={name} beiAenderung={(w) => setName(w as string)} pflicht
            platzhalter="Modellaufnahme Mo–Fr" />

      <Feld
        art="auswahl"
        beschriftung="Exportprofil"
        wert={profil}
        beiAenderung={(w) => setProfil(w as string)}
        optionen={profile.filter((p) => p.versions.length > 0).map((p) => ({ wert: p.id, beschriftung: p.name }))}
        hinweis="Es gilt jeweils die freigegebene Fassung."
        pflicht
      />

      <Feld
        art="mehrfachauswahl"
        beschriftung="Wochentage"
        wert={tage}
        beiAenderung={(w) => setTage(w as string[])}
        optionen={TAGE.map((t, i) => ({ wert: String(i + 1), beschriftung: t }))}
        pflicht
      />

      <div className="m-feld m-feld-halb">
        <label className="m-feld-beschriftung" htmlFor="kette-uhrzeit">Uhrzeit (Ortszeit)</label>
        <input id="kette-uhrzeit" type="time" className="m-feld-eingabe" value={uhrzeit}
               onChange={(e) => setUhrzeit(e.target.value)} />
        <span className="m-feld-hinweis">
          {katalog.zeitzone}{hinweisUtc ? ` - heute ${hinweisUtc}` : ''}. Die Uhrzeit bleibt über die Zeitumstellung.
        </span>
      </div>

      <div className="m-feld m-feld-halb">
        <label className="m-feld-beschriftung" htmlFor="kette-frist">Frist (UTC)</label>
        <input id="kette-frist" type="time" className="m-feld-eingabe" value={frist}
               onChange={(e) => setFrist(e.target.value)} />
        <span className="m-feld-hinweis">
          Danach startet nichts Neues mehr. Gesperrt: {katalog.gesperrteFensterUtc.join(' und ')} UTC.
        </span>
      </div>

      <Feld
        art="zahl"
        beschriftung="Verspätung höchstens"
        einheit="min"
        min={5}
        max={180}
        wert={verspaetung}
        beiAenderung={(w) => setVerspaetung(w as number | null)}
        hinweis="Kommt die Kette später los (Testtor, gesperrtes Fenster), entfällt sie - kein Nachholen."
      />

      <Feld
        beschriftung="Meldung an"
        wert={empfaenger}
        beiAenderung={(w) => setEmpfaenger(w as string)}
        platzhalter="niemand"
        hinweis="Mail nur bei Abweichung (Bremse, Fehler, Variante). Mehrere mit Komma."
      />

      <Feld
        art="schalter"
        beschriftung="Wochenübersicht freitags"
        wert={woche}
        beiAenderung={(w) => setWoche(Boolean(w))}
        hinweis="Freitags eine Übersicht der Woche an dieselben Empfänger (Standard aus)."
      />

      <Feld
        art="schalter"
        beschriftung="Aktiv"
        wert={aktiv}
        beiAenderung={(w) => setAktiv(Boolean(w))}
        hinweis="Stillgelegt läuft nichts planmäßig; Probe und Trockenlauf gehen trotzdem."
      />

      <div style={{ gridColumn: '1 / -1' }}>
        <strong>Schritte in dieser Reihenfolge</strong>
        {aufnahmeNachGesamt && (
          <p className="hinweis warnung" role="alert">
            Eine Aufnahme steht hinter einem Gesamtlauf - die Gesamtläufe rechnen dann mit dem alten Stand.
            Der Server lehnt das ab.
          </p>
        )}
        <ol className="regel-schritte" data-schritte-liste>
          {schritte.map((s, i) => {
            const k = kat.get(s.schluessel);
            const offen = aufgeklappt === s.schluessel;
            return (
              <li key={s.schluessel} data-schritt={s.schluessel}>
                <span style={{ display: 'inline-flex', gap: '0.4rem', alignItems: 'center', flexWrap: 'wrap' }}>
                  <label>
                    <input type="checkbox" checked={s.istAktiv}
                           onChange={(e) => aendern(i, { ...s, istAktiv: e.target.checked })} />{' '}
                    {k?.titel ?? s.schluessel}
                  </label>
                  <span className="sehr-schwach">
                    {k?.art === 'aufnahme' ? `Aufnahme · höchstens ${k.schrittfristMinuten} min` : 'Gesamtlauf'}
                  </span>
                  <button className="m-knopf" aria-label={`${k?.titel ?? s.schluessel} nach oben`} disabled={i === 0}
                          onClick={() => setSchritte(verschieben(schritte, i, -1))}>↑</button>
                  <button className="m-knopf" aria-label={`${k?.titel ?? s.schluessel} nach unten`}
                          disabled={i === schritte.length - 1}
                          onClick={() => setSchritte(verschieben(schritte, i, 1))}>↓</button>
                  {k && (k.laeufe.length > 0 || k.teile.length > 1) && (
                    <button className="m-knopf" onClick={() => setAufgeklappt(offen ? null : s.schluessel)}>
                      {offen ? 'Zuklappen' : 'Einzelheiten'}
                    </button>
                  )}
                  <button className="m-knopf" onClick={() => setSchritte(schritte.filter((_, j) => j !== i))}>
                    Herausnehmen
                  </button>
                </span>
                {offen && k && (
                  <div className="regel-schritt-einzelheiten" style={{ marginLeft: '1.5rem' }}>
                    {k.teile.length > 1 && (
                      <div>
                        <span className="schwach">Teilaufnahmen:</span>
                        {k.teile.map((t) => (
                          <label key={t.schluessel} style={{ marginLeft: '0.75rem' }}>
                            <input
                              type="checkbox"
                              data-teil={t.schluessel}
                              checked={!s.teileAus.includes(t.schluessel)}
                              onChange={(e) => aendern(i, {
                                ...s,
                                teileAus: e.target.checked
                                  ? s.teileAus.filter((x) => x !== t.schluessel)
                                  : [...s.teileAus, t.schluessel],
                              })}
                            />{' '}{t.titel}
                          </label>
                        ))}
                      </div>
                    )}
                    {k.laeufe.length > 0 && (
                      <ul className="regel-folgelaeufe">
                        {k.laeufe.map((l) => (
                          <li key={l.schluessel}>
                            <label>
                              <input
                                type="checkbox"
                                data-lauf={l.schluessel}
                                checked={laufAktiv(s, l)}
                                onChange={(e) => aendern(i, laufSchalten(s, l, e.target.checked))}
                              />{' '}{l.titel}
                            </label>
                            {l.braucht && l.braucht.length > 0 && (
                              <span className="sehr-schwach"> · nach {l.braucht.join(', ')}</span>
                            )}
                            {l.bremsfaehig && <span className="sehr-schwach"> · mit Bremse</span>}
                            {l.ausGrund && <div className="sehr-schwach">ab Werk aus: {l.ausGrund}</div>}
                          </li>
                        ))}
                      </ul>
                    )}
                  </div>
                )}
              </li>
            );
          })}
        </ol>
        {frei.length > 0 && (
          <span style={{ display: 'inline-flex', gap: '0.5rem', alignItems: 'center' }}>
            <select className="m-feld-eingabe" aria-label="Schritt hinzufügen" value={neuerSchritt}
                    onChange={(e) => setNeuerSchritt(e.target.value)}>
              <option value="">Schritt hinzufügen …</option>
              {frei.map((k) => <option key={k.schluessel} value={k.schluessel}>{k.titel}</option>)}
            </select>
            <button
              className="m-knopf"
              disabled={!neuerSchritt}
              onClick={() => {
                const k = kat.get(neuerSchritt);
                if (!k) return;
                setSchritte([...schritte, leererSchritt(k)]);
                setNeuerSchritt('');
              }}
            >
              Hinzufügen
            </button>
          </span>
        )}
      </div>
    </FormSection>
  );
}
